import { useMemo } from "react";
import useTranslator from "../../hooks/useTranslator";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import { getRentPercentage } from "../../helper/common";
import styles from "./TargetRent.module.css";

const TargetRent = ({
  monthlyIncome = 0,
  rent = 0,
  onRentChange,
  onAddGuarantor,
  onAddFlatmate,
  hasGuarantor = false,
  hasFlatmate = false,
}) => {
  const { _t } = useTranslator();
  const min = 10;
  const max = 50;

  const effortRate = useMemo(() => {
    if (!monthlyIncome) return 35;
    const p = getRentPercentage(monthlyIncome, rent)?.exactValue;
    return Math.min(max, Math.max(min, Math.round(p) || min));
  }, [monthlyIncome, rent]);

  const comfortableMax = useMemo(
    () => Number(((monthlyIncome * 35) / 100).toFixed(2)),
    [monthlyIncome]
  );
  const tighterMax = useMemo(
    () => Number(((monthlyIncome * 45) / 100).toFixed(2)),
    [monthlyIncome]
  );
  const isPast = rent > comfortableMax;
  const fmt = (n) =>
    new Intl.NumberFormat("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  const handleSliderChange = (val) => {
    if (!monthlyIncome) {
      return;
    }
    onRentChange?.(Number(((monthlyIncome * val) / 100).toFixed(2)));
  };
  const maxRange = Math.max(tighterMax * 1.2, rent * 1.1, 1);
  const percent = Math.min(100, Math.max(0, (rent / maxRange) * 100));

  return (
    <section
      className={`tenant-profile-placeholder-card tenant-profile-placeholder-card--target-rent ${styles.card}`}
      aria-labelledby="target-rent-title"
    >
      <h2 id="target-rent-title" className={styles.title}>
        {_t("yourRentalBarometer", "Your rental barometer")}
      </h2>
      <p className={styles.subtitle}>
        {isPast
          ? _t(
              "rentalBarometerPastComfortable",
              `At €${fmt(rent)}/month, you're past the comfortable range for your income.`,
              undefined,
              { rent: fmt(rent) }
            )
          : _t(
              "rentalBarometerComfortable",
              `At €${fmt(rent)}/month, you're within the comfortable range for your income.`,
              undefined,
              { rent: fmt(rent) }
            )}
      </p>
      <label className={styles.label}>
        {_t("enterRentConsidering", "Enter the rent you're considering")}
      </label>
      <div className={styles.inputWrap}>
        <span>€</span>
        <input
          type="number"
          value={rent}
          min={0}
          onChange={(e) =>
            onRentChange?.(Math.max(0, Number(e.target.value) || 0))
          }
          aria-label={_t("targetRent", "Target Rent")}
        />
        <span className={styles.suffix}>/month</span>
      </div>
      <div className={styles.meterWrap}>
        <span className={styles.valueLabel} style={{ left: `${percent}%` }}>
          €{fmt(rent)}
        </span>
        <div
          className={`skormeter ${styles.meter}`}
          style={{ "--w": ((effortRate - min) / (max - min)) * 100 }}
        >
          <Slider
            min={min}
            max={max}
            value={effortRate}
            onChange={handleSliderChange}
            disabled={!monthlyIncome}
          />
          <div className="slider-labels">
            <span>{_t("comfortable", "Comfortable")}</span>
            <span></span>
            <span>{_t("moderate", "Moderate")}</span>
            <span></span>
            <span>{_t("elevated", "Elevated")}</span>
          </div>
        </div>
      </div>
      <div className={styles.scale}>
        <span>
          {_t("comfortableUpTo", "Comfortable · up to")} €{fmt(comfortableMax)}
        </span>
        <span>
          {_t("tighterAbove", "Tighter · above")} €{fmt(comfortableMax)}
          {tighterMax > comfortableMax ? ` · up to €${fmt(tighterMax)}` : ""}
        </span>
      </div>
      {isPast && !(hasGuarantor && hasFlatmate) && (
        <div className={styles.callout}>
          <p>
            {_t(
              "rentalBarometerCallout",
              "A guarantor or flatmate can help offset this and keep your application strong."
            )}
          </p>
          <div className={styles.actions}>
            {!hasGuarantor && (
              <button
                type="button"
                className={styles.primary}
                onClick={onAddGuarantor}
              >
                {_t("addGuarantor", "Add guarantor")}
              </button>
            )}
            {!hasFlatmate && (
              <button
                type="button"
                className={styles.secondary}
                onClick={onAddFlatmate}
              >
                {_t("addFlatmate", "Add flatmate")}
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default TargetRent;
